import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { TaskDependencyEdge, TaskDependencyTask } from '../../../types/TaskDependency';
import { TaskType } from '../../../types/TaskType';
import { TaskTypeLabel } from '../Dropdown/TaskTypeDropdown';
import { GoToIcon } from '../../../assets/CustomIcons/GoToIcon';
import CustomTooltip from './CustomTooltip';

type TaskDependencyTooltipProps = {
  edges: TaskDependencyEdge[];
  onOpenTask: (task: TaskDependencyTask) => void;
  onRemove: (dependencyId: number, relatedTaskId: number) => void;
  children: React.ReactNode;
};

/**
 * Lists every linked task on hover, each row offering "go to" and "remove". (#2599)
 *
 * Builds on CustomTooltip so the surface follows the app's AntD theme in both light and
 * dark mode, and only re-enables pointer events — CustomTooltip disables them, which would
 * otherwise make the per-row actions unclickable. Row content inherits the tooltip's own
 * text colour rather than hardcoding one, since that colour flips with the theme.
 */
const TaskDependencyTooltip = ({ edges, onOpenTask, onRemove, children }: TaskDependencyTooltipProps) => {
  return (
    <CustomTooltip
      // Long enough to travel from the cell onto the overlay without it closing.
      mouseLeaveDelay={0.2}
      overlayStyle={{ pointerEvents: 'auto' }}
      title={
        <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
          {edges.map((edge) => (
            <div key={edge.id} className="flex items-center gap-2 h-6 min-w-0">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <TaskTypeLabel type={edge.task.type as TaskType} size="small" />
                <span className="truncate text-sm leading-5">{edge.task.title}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <GoToIcon
                  aria-label={`Go to ${edge.task.title}`}
                  className="text-neutral-900 bg-neutral-300 rounded p-1 cursor-pointer hover:bg-neutral-400"
                  onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onOpenTask(edge.task);
                  }}
                />
                <CloseOutlined
                  aria-label={`Remove ${edge.task.title}`}
                  className="p-0.5 cursor-pointer hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove(edge.id, edge.task.id);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      }
    >
      {children}
    </CustomTooltip>
  );
};

export default TaskDependencyTooltip;
